export type GuardConfig = {
  jsonSchema?: object;
  banPhrases?: string[];
  minGroundingScore?: number;
  sourceText?: string;
};

export interface Guard {
  verify(response: string, config: GuardConfig): { isValid: boolean; errors: string[] };
}

function repairJson(text: string): string {
  let s = text.trim();
  if (s.startsWith("```")) {
    s = s.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "");
  }
  s = s.replace(/,\s*([}\]])/g, "$1");
  return s;
}

function tokenize(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .split(/\W+/)
      .filter((t) => t.length > 2),
  );
}

function groundingScore(response: string, source: string): number {
  const resp = tokenize(response);
  const src = tokenize(source);
  if (resp.size === 0) return 0;
  let overlap = 0;
  for (const t of resp) {
    if (src.has(t)) overlap++;
  }
  return overlap / resp.size;
}

function validateJsonSchema(value: unknown, schema: object): string[] {
  const s = schema as { type?: string; required?: string[]; properties?: Record<string, unknown> };
  const errors: string[] = [];

  if (s.type === "object" && (value === null || typeof value !== "object" || Array.isArray(value))) {
    errors.push("Expected object");
    return errors;
  }

  if (s.type === "object" && s.required) {
    const obj = value as Record<string, unknown>;
    for (const key of s.required) {
      if (!(key in obj)) errors.push(`Missing required field: ${key}`);
    }
  }

  return errors;
}

export function createGuard(): Guard {
  return {
    verify(response: string, config: GuardConfig) {
      const errors: string[] = [];

      if (config.banPhrases?.length) {
        const lower = response.toLowerCase();
        for (const phrase of config.banPhrases) {
          if (lower.includes(phrase.toLowerCase())) {
            errors.push(`Response contains banned phrase: ${phrase}`);
          }
        }
      }

      if (config.jsonSchema) {
        try {
          const repaired = repairJson(response);
          const parsed = JSON.parse(repaired);
          errors.push(...validateJsonSchema(parsed, config.jsonSchema));
        } catch {
          errors.push("Response is not valid JSON");
        }
      }

      if (config.minGroundingScore !== undefined && config.sourceText) {
        const score = groundingScore(response, config.sourceText);
        if (score < config.minGroundingScore) {
          errors.push(
            `Grounding score ${score.toFixed(2)} below threshold ${config.minGroundingScore}`,
          );
        }
      }

      return { isValid: errors.length === 0, errors };
    },
  };
}
